import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Save, AlertCircle, CheckCircle2, Lock, Mail } from 'lucide-react'
import { authAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import PageHeader from '@/components/PageHeader'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'

/* Account-instellingen — e-mail en wachtwoord.
   Displayname en avatar horen NIET hier — die bewerk je op je eigen
   profielpagina (Steam-model: profiel = wat anderen zien, instellingen =
   de dingen die alleen jij nodig hebt om in te loggen). */
export default function SettingsPage() {
  const navigate = useNavigate()
  const { user: currentUser, updateUser } = useAuth()

  const [form, setForm] = useState({
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return }
    setForm(p => ({ ...p, email: currentUser.email || '' }))
  }, [currentUser, navigate])

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
    setError(''); setSuccess('')
  }

  const save = async (e) => {
    e.preventDefault()
    setError(''); setSuccess('')
    if (form.newPassword || form.currentPassword) {
      if (!form.currentPassword) return setError('Huidig wachtwoord is nodig om je wachtwoord te wijzigen')
      if (!form.newPassword || form.newPassword.length < 8) return setError('Nieuw wachtwoord minimaal 8 tekens')
      if (form.newPassword !== form.confirmNewPassword) return setError('Nieuwe wachtwoorden komen niet overeen')
    }
    setSaving(true)
    try {
      const update = { email: form.email }
      if (form.newPassword) {
        update.currentPassword = form.currentPassword
        update.newPassword = form.newPassword
      }
      const r = await authAPI.updateProfile(update)
      updateUser(r.data.user)
      setSuccess('Opgeslagen')
      setForm(p => ({ ...p, currentPassword: '', newPassword: '', confirmNewPassword: '' }))
    } catch (err) {
      setError(err.response?.data?.error || 'Opslaan mislukt')
    } finally { setSaving(false) }
  }

  if (!currentUser) return null

  return (
    <div className="max-w-xl mx-auto p-6 space-y-6">
      <PageHeader
        title="Account"
        subtitle="Waar we je meldingen sturen en waarmee je inlogt"
      />

      {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
      {success && <Alert><CheckCircle2 className="h-4 w-4" /><AlertDescription>{success}</AlertDescription></Alert>}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">E-mail en wachtwoord</CardTitle>
          <CardDescription>Je publieke naam en avatar wijzig je op je profielpagina.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label className="flex items-center gap-1"><Mail size={12} /> E-mail</Label>
              <Input name="email" type="email" value={form.email} onChange={onChange} required />
            </div>
            <div className="pt-3 border-t space-y-3">
              <div className="text-sm font-medium flex items-center gap-1"><Lock size={14} /> Wachtwoord wijzigen (optioneel)</div>
              <Input name="currentPassword" type="password" value={form.currentPassword} onChange={onChange} placeholder="Huidig wachtwoord" autoComplete="current-password" />
              <Input name="newPassword" type="password" value={form.newPassword} onChange={onChange} placeholder="Nieuw wachtwoord" autoComplete="new-password" />
              <Input name="confirmNewPassword" type="password" value={form.confirmNewPassword} onChange={onChange} placeholder="Herhaal nieuw wachtwoord" autoComplete="new-password" />
            </div>
            <Button type="submit" disabled={saving}>
              <Save size={14} className="mr-1" /> {saving ? 'Opslaan…' : 'Opslaan'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground text-center">
        Ga naar je{' '}
        <button className="underline" onClick={() => navigate(`/spelers/${currentUser.id}`)}>
          profielpagina
        </button>{' '}
        om je naam, avatar of bio te wijzigen.
      </div>
    </div>
  )
}
